import { EnumButtonName } from '@calm/evexia-analytics';
import ValidatedTextField, {
    ValidatedTextFieldProps,
} from '@rhg/components/validated-text-field';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Button,
    DialogActions,
    Typography,
    type TextField,
    type TextFieldProps,
} from '@rhg/libs/material';
import {
    LocalizationProvider,
    DatePickerProps,
    DatePicker,
    PickersActionBarProps,
} from '@rhg/libs/material/date-pickers';
import { displayLocaleCustomString } from '@rhg/utils/date-time';
import { useMemo } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

const stylePopper = {
    width: { xs: '100%', sm: '100%', md: 'auto' },
};
const stylePaper = {
    backgroundColor: 'background.paper',
};

interface Props {
    name: string;
    control: Control<any>;
    label: string;
    format?: string;
    required?: boolean;
    textFieldProps?: Partial<ValidatedTextFieldProps>;
    datePickerProps?: Partial<DatePickerProps<unknown>>;
}

const styleActionBarContainer = { width: '100%', mb: 4, mx: 2 };
const styleActionBarPreviewTextContainer = {
    my: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};
const styleActionBarButton = { textTransform: 'uppercase' };

const CustomActionBar = ({
    value,
    label,
    className,
    onAccept,
}: PickersActionBarProps & { value?: string | null; label?: string }) => {
    const { formatMessage } = useIntl();

    const formattedDate = useMemo(() => {
        if (!value) {
            return '';
        }

        try {
            return displayLocaleCustomString(
                {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                },
                new Date(value)
            );
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            // Handle invalid time value and other date errors
            return '';
        }
    }, [value]);

    const formattedLabel = useMemo(
        () => (label ? label.replace('(Required)', '').trim() : ''),
        [label]
    );
    if (!formattedDate) {
        return null;
    }
    return (
        <DialogActions className={className}>
            <Box sx={styleActionBarContainer}>
                <Box sx={styleActionBarPreviewTextContainer}>
                    <Typography variant="subtitle2" color="text.sub">
                        {formattedLabel}
                    </Typography>
                    <Typography variant="body1Demi">{formattedDate}</Typography>
                </Box>
                <Button
                    onClick={onAccept}
                    trackButton={EnumButtonName.Confirm}
                    fullWidth
                    variant="contained"
                    size="small"
                    sx={styleActionBarButton}
                >
                    {formatMessage(commonMessages['common.ok'])}
                </Button>
            </Box>
        </DialogActions>
    );
};

const FormDatePicker = ({
    name,
    control,
    label,
    textFieldProps,
    datePickerProps,
    format,
    required = false,
}: Props) => {
    return (
        <LocalizationProvider>
            <Controller
                name={name}
                control={control}
                render={(props) => (
                    <DatePicker
                        {...datePickerProps}
                        {...props}
                        format={format}
                        value={props.field.value || null}
                        onChange={props.field.onChange}
                        closeOnSelect={false}
                        disableHighlightToday
                        slotProps={{
                            popper: { sx: stylePopper },
                            desktopPaper: { sx: stylePaper },
                            mobilePaper: { sx: stylePaper },
                            textField: {
                                name,
                                required,
                                label,
                                error: !!props.fieldState.error,
                                errorText: props.fieldState.error?.message,
                                ...textFieldProps,
                            } as TextFieldProps,
                            actionBar: {
                                actions: ['accept'],
                                value: props.field.value || null,
                                label,
                            } as unknown as PickersActionBarProps,
                            toolbar: { hidden: true },
                        }}
                        slots={{
                            textField: ValidatedTextField as typeof TextField,
                            actionBar: CustomActionBar,
                        }}
                    />
                )}
            />
        </LocalizationProvider>
    );
};

export default FormDatePicker;
